import { faExternalLink } from '@fortawesome/pro-regular-svg-icons/faExternalLink';
import { faTimes } from '@fortawesome/pro-light-svg-icons/faTimes';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import Attachments from 'components/admin/knowledge_base/snippet/snippet_attachments';
import { SimpleAnswerContentBody } from 'components/customer/answer_panel/preview/content/answer_preview_content_body';
import { useExecuteAction } from 'components/hooks/connect_hooks';
import connect from 'components/lib/connect';
import ModalCard from 'components/common/modal_card';
import NavigateToSnippet from 'actions/knowledge_base/navigate_to_snippet';
import PortalModalWrapper from 'components/lib/portal_modal_wrapper';
import Snippet, { SnippetChannel, SnippetContentType } from 'models/answers/snippet';
import Spinner from 'components/common/spinner';

const CHANNEL_PRIORITY = [
  SnippetChannel.INFO,
  SnippetChannel.ANY_CHANNEL,
  SnippetChannel.MESSAGE,
  SnippetChannel.SELF_SERVICE,
];

function TeamAssistAnswerPreviewModal({ answer, canViewInKb, isLoading, onClose }) {
  const executeAction = useExecuteAction();
  const best = answer ? findBestChannel(answer) : null;

  return (
    <PortalModalWrapper>
      <Modal>
        <ModalCard data-aid="teamAssistAnswerPreviewModal" onClose={onClose}>
          <Close onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </Close>
          {isLoading ? (
            <SpinnerWrapper>
              <Spinner size={32} />
            </SpinnerWrapper>
          ) : answer ? (
            <>
              <Header>
                <TitleRow>
                  <Name>{answer.name}</Name>
                  {canViewInKb && (
                    <KbLinkButton
                      data-aid="teamAssistAnswerPreviewModal-kbLink"
                      onClick={() => {
                        executeAction(NavigateToSnippet, answer.id);
                        onClose();
                      }}
                      title="Open in Knowledge Base"
                    >
                      <FontAwesomeIcon icon={faExternalLink} />
                    </KbLinkButton>
                  )}
                </TitleRow>
              </Header>
              {best && (
                <PreviewBodyWrapper>
                  <SimpleAnswerContentBody
                    aid="teamAssistAnswerPreviewModal-body"
                    bodyHtml={best.bodyHtml}
                    id={`${answer.id}-${best.channel}`}
                    isInternal={best.channel === SnippetChannel.INFO}
                  />
                </PreviewBodyWrapper>
              )}
              {best && <Attachments contentChannelType={best.channel} isRemoveable={false} snippet={answer} />}
            </>
          ) : (
            <SpinnerWrapper>
              <NoAnswerState>No answer preview is available.</NoAnswerState>
            </SpinnerWrapper>
          )}
        </ModalCard>
      </Modal>
    </PortalModalWrapper>
  );
}

TeamAssistAnswerPreviewModal.propTypes = {
  answer: PropTypes.instanceOf(Snippet),
  canViewInKb: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

function mapStateToProps({ getProvider, isFeatureEnabled }) {
  const teamAssistProvider = getProvider('teamAssistPanel');
  const teamAssistPanel = teamAssistProvider.get();
  const isLoading = teamAssistProvider.isLoading();
  const canViewInKb = isFeatureEnabled('answers:admin') || isFeatureEnabled('answers:read');

  return {
    answer: teamAssistPanel ? teamAssistPanel.answer : null,
    canViewInKb,
    isLoading,
  };
}

function findBestChannel(answer) {
  const content = answer.findContentByLanguage('en-us');
  if (!content) return null;

  for (const channel of CHANNEL_PRIORITY) {
    const bodyHtml = content.getBodyByType(SnippetContentType[channel]);
    if (bodyHtml) {
      return { channel, bodyHtml };
    }
  }
  return null;
}

export default connect(mapStateToProps)(TeamAssistAnswerPreviewModal);

const Modal = styled.div`
  .modalCard-content {
    display: flex;
    flex-direction: column;
    max-height: 80vh;
    max-width: 70vw;
    overflow: auto;
    position: relative;
  }
`;

const Close = styled.div`
  cursor: pointer;
  font-size: 16px;
  padding: 8px 12px;
  position: absolute;
  right: 0;
  top: 0;

  &:hover {
    color: ${p => p.theme.colors.gray600};
  }
`;

const Header = styled.div`
  align-items: center;
  display: flex;
  margin-bottom: ${p => p.theme.spacing.large};
`;

const TitleRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const Name = styled.div`
  font-size: ${p => p.theme.fontSize.large};
  font-weight: bold;
  line-height: ${p => p.theme.fontSize.large};
`;

const KbLinkButton = styled.button`
  align-items: center;
  background: none;
  border: none;
  color: ${p => p.theme.colors.gray500};
  cursor: pointer;
  display: flex;
  font-size: 14px;
  padding: 4px 6px;

  &:hover {
    color: ${p => p.theme.colors.green400};
  }
`;

const PreviewBodyWrapper = styled.div`
  .slateTextEditor {
    padding: 0;
  }
`;

const SpinnerWrapper = styled.div`
  align-items: center;
  display: flex;
  flex: 1;
  justify-content: center;
  min-height: 100px;
`;

const NoAnswerState = styled.div`
  color: ${p => p.theme.colors.gray600};
  font-size: 14px;
  text-align: center;
`;
